import { useCallback, useMemo, useRef, useState } from "react";

import clsx from "clsx";
import {
  cloneDeep,
  compact,
  flatten,
  groupBy,
  isNil,
  keyBy,
  orderBy,
} from "lodash";
import { useToasts } from "react-toast-notifications";
import { useUpdateEffect } from "usehooks-ts";
import { v4 as uuidv4 } from "uuid";

import { useUser } from "@components/user-context";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import { useQueryMetadata } from "@components/v2/modals";
import { CustomMetrics } from "@features/custom-metrics";
import { useFeatureFlags } from "@hooks/http";
import { usePrevious } from "@hooks/use-previous";
import {
  type GetSalesReportV3Augmented_me_salesTableReportV3Augmented_actual_rows,
  SalesTableTypeEnum,
  type CreativesStatusEnum,
  type SalesPageMetric,
  type GetSalesReportV3AugmentedVariables,
} from "@north-beam/nb-client-side";
import {
  metrics,
  type CurrencyCode,
  type DbForecastV1DateRow,
} from "@north-beam/nb-common";
import { IconButton } from "@shared/buttons";

import { type Dimensions } from "../charts/chart";
import { useCustomGoals, usePageConfiguration } from "../hooks";
import { useAvailableMetrics } from "../hooks/use-available-metrics";
import { SALES_TABLE_LIMIT, useLoadSalesData } from "../hooks/use-sales-report";
import {
  useActiveTabState,
  useTabState,
  type FiltersState,
  type TabState,
} from "../hooks/use-tab-state";
import { useTableColumns } from "../hooks/use-table-columns";
import {
  useDefaultTableSelectionIsSelectAll,
  useSplitView,
  useTableRowSelection,
} from "../hooks/use-table-selection";
import { SearchForm } from "../search-form";
import { Tabs, type TabId } from "../tabs";
import {
  enrichInclusionsAndExclusions,
  reconcileAppliedAndProposedTabFilters,
} from "../tabs/components/tabs";
import { ConfigurationDetailsModal } from "../top-level-filters/components/configuration-details-modal";
import { ExportTableModal } from "../top-level-filters/components/export-menu";
import { type TopLevelBreakdown } from "../types";
import { type SalesTableRow, type SalesTableVisualRow } from "../types/table";
import { deriveComparisonDateRange } from "../utils/compare-dates-helpers";

import { FIXED_NAME_COLUMN_ID } from "./components/constants";
import { CustomizeTableModal } from "./components/customize-table-modal";
import { Table } from "./components/table";
import { TableViewSelector } from "./components/table-view-selector";
import { useExpandedRowIds, useTabTopPlatforms } from "./contexts";
import { useSalesTableBenchmarks, type SalesTableBenchmarks } from "./hooks";
import { processPreloadedData } from "./utils";

const defaultEmptyTableRow = ({
  tab,
}: {
  tab: SalesTableVisualRow["rowMetadata"]["tab"];
}): SalesTableVisualRow => {
  return {
    id: "default",
    index: 0,
    isHeaderRow: true,
    hasChildren: false,
    key: "default",
    metrics: {},
    display: {},
    changeFractions: {},
    children: [],
    dailyMetrics: null,

    rowMetadata: {
      dimensions: {},
      statuses: [],
      platform: "",
      tab,
      point: {},
      naming: {},
      imageUri: null,
      breakdown: null,
      deepestAtomicityCounts: 0,
    },
    export: {},
    rowType: "normal",
  };
};

const constructSalesDictionary = (
  indent: number,
  observerRows: SalesTableRow[],
  salesDictionaryReference: SalesRowDictionary,
) => {
  if (indent === 0) {
    return salesDictionaryReference;
  }
  observerRows.forEach((row, index) => {
    if (row.rowType === "loadMore" || row.rowType === "loading") {
      return null;
    }
    salesDictionaryReference[row.key] = {
      id: row.id,
      index,
      children: constructSalesDictionary(indent - 1, row.children, {}),
    };
  });
  return salesDictionaryReference;
};

const processTableData = (
  ad: GetSalesReportV3Augmented_me_salesTableReportV3Augmented_actual_rows,
  nbOnlyMetricsArray: SalesPageMetric[],
  customMetrics: SalesPageMetric[],
) => {
  // TODO: TYPE AS METRIC POINT TO AVOID ANY TYPES
  const agg: Record<string, number> = {};
  const commonElements = {
    key: ad.key,
    metrics: agg,
    platform: ad.dimensions["Platform (Northbeam)"],
    statuses: ad.statuses,
    naming: {
      name: ad.name ?? "N/A",
      campaignName: ad.campaignName ?? "N/A",
      adsetName: ad.adsetName ?? "N/A",
      adName: ad.adName ?? "N/A",
    },
    imageUri: ad.imageUri,
    dimensions: ad.dimensions,
    deepestAtomicityCounts: ad.deepestAtomicityCounts,
    dailyMetrics: ad.dailyMetrics,
  };

  if (!ad.dailyMetrics?.length) {
    return commonElements;
  }

  const forecastV1Daily = ad.forecastV1Daily;

  const dateToForecastRow: Record<string, DbForecastV1DateRow> = {};
  if (forecastV1Daily) {
    for (const forecastRow of forecastV1Daily) {
      dateToForecastRow[forecastRow.date] = forecastRow;
    }
  }

  const aggregatedMetrics = ad.dailyMetrics
    .slice(1, ad.dailyMetrics.length)
    ?.reduce((acc, dailyRow) => {
      const metricPoint = metrics.makeMetricPoint(
        dailyRow,
        dateToForecastRow[dailyRow.date],
      );
      metrics.addToMetricPoint(acc, metricPoint);
      return acc;
      /* eslint-disable-next-line max-len */
    }, metrics.makeMetricPoint(cloneDeep(ad.dailyMetrics[0]), dateToForecastRow[ad.dailyMetrics[0].date]));

  nbOnlyMetricsArray.forEach((metric) => {
    if (!agg[metric.id]) {
      agg[metric.id] = 0;
    }
    agg[metric.id] = metric.calculate(aggregatedMetrics);
  });

  /*  Needs all metrics to be calculated before calculating custom metrics
     using the aggregated metrics that have been precalculated! */
  customMetrics.forEach((metric) => {
    if (!agg[metric.id]) {
      agg[metric.id] = 0;
    }
    // TODO: Fix this type so we don't coerce it.
    agg[metric.id] = metric.calculate(agg as metrics.MetricPoint);
  });

  return commonElements;
};

const SALES_TABLE_BENCHMARK_SPEC: Record<
  string,
  { metric: "cac" | "roas"; type: "blended" | "firstTime" | "returning" }
> = {
  benchmarkCAC: { metric: "cac", type: "blended" },
  benchmarkCACft: { metric: "cac", type: "firstTime" },
  benchmarkCACrtn: { metric: "cac", type: "returning" },
  benchmarkROAS: { metric: "roas", type: "blended" },
  benchmarkROASft: { metric: "roas", type: "firstTime" },
  benchmarkROASrtn: { metric: "roas", type: "returning" },
};

function getBenchmarkData(
  benchmarks: SalesTableBenchmarks,
  platform: string,
  metric: "cac" | "roas",
  type: "blended" | "firstTime" | "returning",
) {
  return benchmarks[platform]?.[metric]?.[type];
}

function enhanceRowWithBenchmarks(
  row: SalesTableVisualRow,
  salesTableBenchmarks: SalesTableBenchmarks,
  metricsById: { [metricId: string]: SalesPageMetric },
  code: CurrencyCode,
): SalesTableVisualRow {
  const benchmarks: {
    [metricId: string]: {
      value: number;
      percentDisplay: string;
      valueDisplay: string;
    };
  } = {};

  Object.entries(row.metrics).forEach(([metricId, metricValue]) => {
    const metric = metricsById[metricId];
    const benchmarkSpec = SALES_TABLE_BENCHMARK_SPEC[metricId];

    if (benchmarkSpec) {
      const benchmark = getBenchmarkData(
        salesTableBenchmarks,
        row.rowMetadata.platform,
        benchmarkSpec.metric,
        benchmarkSpec.type,
      );

      if (benchmark && metric.benchmarkFormatter) {
        const percent = (metricValue - benchmark) / benchmark;
        const valueDisplay = metric.formatter(benchmark, code, {
          precision: "exact",
        });
        const percentDisplay = metric.benchmarkFormatter?.(
          Math.abs(percent),
          {},
        );
        benchmarks[metricId] = { value: percent, percentDisplay, valueDisplay };
      }
    }
  });

  return {
    ...row,
    children: row.children.map((child) =>
      child.rowType === "normal"
        ? enhanceRowWithBenchmarks(
            child,
            salesTableBenchmarks,
            metricsById,
            code,
          )
        : child,
    ),
    benchmarks,
  };
}

type SalesDataRow = {
  key: string;
  dailyMetrics: any[] | null;
  metrics: Record<string, number>;
  platform: string;
  deepestAtomicityCounts: number;
  statuses: CreativesStatusEnum[] | null;
  dimensions: { [dimension: string]: string };
  imageUri: string | null;
  naming: {
    name: string;
    campaignName: string;
    adsetName: string;
    adName: string;
  };
};

const transform = ({
  row,
  breakdowns,
  tab,
  metricsById,
  currentBreakdown,
  code,
  compareRow,
}: {
  row: SalesDataRow;
  breakdowns: TopLevelBreakdown[];
  tab: "breakdown" | "campaign" | "adset" | "ad";
  metricsById: { [metricId: string]: SalesPageMetric };
  currentBreakdown: TopLevelBreakdown | null;
  code: CurrencyCode;
  compareRow?: Pick<SalesDataRow, "metrics">;
}): SalesTableVisualRow => {
  const display: { [metricId: string]: string } = {};
  const changeFractions: { [metricId: string]: number } = {};

  Object.entries(row.metrics).forEach(([metricId, metricValue]) => {
    const metric = metricsById[metricId];
    display[metricId] = metric.formatter(metricValue, code, {
      precision: "exact",
    });

    if (compareRow) {
      const current = row.metrics[metricId];
      const compare = compareRow.metrics[metricId];
      const changeFraction = (current - compare) / compare;
      changeFractions[metricId] = changeFraction;
    }
  });

  const rowKeyParsed = JSON.parse(row.key);
  let hasChildren: boolean;
  if (tab !== "breakdown") {
    hasChildren = Array.isArray(rowKeyParsed);
  } else {
    const maxLength = Object.keys(row.dimensions);

    if (!breakdowns?.length) {
      hasChildren = false;
    } else {
      if (rowKeyParsed.length === maxLength) {
        hasChildren = false;
      } else {
        hasChildren = rowKeyParsed.length !== breakdowns.length;
      }
    }
  }

  return {
    id: `${tab}-${row.key}`,
    index: 0,
    rowType: "normal",
    key: row.key,
    metrics: row.metrics,
    display,
    changeFractions,
    children: [],
    dailyMetrics: row.dailyMetrics,
    rowMetadata: {
      dimensions: row.dimensions,
      statuses: row.statuses,
      tab,
      point: row.metrics,
      naming: row.naming,
      imageUri: row.imageUri,
      platform: row.platform,
      deepestAtomicityCounts: row.deepestAtomicityCounts,
      breakdown: currentBreakdown?.key ?? null,
    },
    export: row.metrics,
    hasChildren,
  };
};

type SalesRowDictionary = {
  [rowId: string]: SalesRowReference;
};

type SalesRowReference = {
  id: string;
  index: number;
  children: SalesRowDictionary;
};

function findMetadataPaging(
  breakdownPath: string[],
  salesRowDictionary: SalesRowDictionary,
  rows: SalesTableRow[],
): { rows: SalesTableRow[]; metadata: SalesRowReference } | null {
  let referenceMetadataRow: SalesRowReference | null = null;
  let referenceRows: SalesTableRow[] = rows;

  if (!breakdownPath?.length) {
    return null;
  }

  breakdownPath.forEach((_, index) => {
    const completePath = breakdownPath.slice(0, index + 1);

    referenceMetadataRow = (referenceMetadataRow?.children ??
      salesRowDictionary)[JSON.stringify(completePath)];

    referenceRows =
      (referenceRows?.[referenceMetadataRow?.index] as SalesTableVisualRow)
        ?.children ?? [];
  });

  return {
    rows: referenceRows,
    metadata: referenceMetadataRow as unknown as SalesRowReference,
  };
}

interface Props {
  isConfigurationDetailsModalOpen: boolean;
  onCloseConfigurationDetailsModal(): void;
  hideCustomMetrics?: boolean;
  hideExportTable?: boolean;
  hideCustomizeTable?: boolean;
  hideExtraOptions?: boolean;
  hideSearchForm?: boolean;
  hideStatusColumn?: boolean;
}

export function SalesTable({
  isConfigurationDetailsModalOpen,
  onCloseConfigurationDetailsModal,
  hideCustomMetrics,
  hideExportTable,
  hideCustomizeTable,
  hideExtraOptions,
  hideSearchForm,
  hideStatusColumn,
}: Props) {
  const {
    attributionModel,
    breakdowns,
    compareDateRange,
    dateRangeInterval,
    setGranularityAndActiveTab,
    timeGranularity,
    sorting,
    sortingOrder,
  } = usePageConfiguration();

  const { addToast } = useToasts();

  const prettyLogEventPage = usePrettyLogEventPage();
  const adTabState = useTabState("ad");
  const adsetTabState = useTabState("adset");
  const campaignTabState = useTabState("campaign");
  const breakdownTabState = useTabState("breakdown");
  const tabStates = useMemo(
    () => ({
      breakdown: breakdownTabState,
      campaign: campaignTabState,
      adset: adsetTabState,
      ad: adTabState,
    }),
    [breakdownTabState, campaignTabState, adsetTabState, adTabState],
  );
  const isSplitView = useSplitView();
  const { loading: featureFlagDataLoading } = useFeatureFlags();
  const { isCustomGoalsDataLoading } = useCustomGoals();

  const { user } = useUser();
  const { tabState, setTabState, activeTab } = useActiveTabState();
  const [, setTabTopPlatforms] = useTabTopPlatforms(activeTab);
  const { addQueryMetadata } = useQueryMetadata();

  const defaultRow = useMemo(
    () => defaultEmptyTableRow({ tab: activeTab }),
    [activeTab],
  );

  const [expandedRowIds, setExpandedRowIds] = useExpandedRowIds();

  const salesRowDictionary = useRef<SalesRowDictionary>({});

  const [rows, setRows] = useState<SalesTableRow[]>([]);

  const { hasFeatureFlag } = useFeatureFlags();

  const { metrics, nbOnlyMetrics, customMetrics } = useAvailableMetrics();
  const { columns } = useTableColumns(metrics, { hideStatusColumn });

  const selectedMetrics = useMemo(
    () => compact(columns?.map((c) => c.metric) ?? []),
    [columns],
  );

  const {
    loadSalesTable,
    loading: loadingTable,
    abort,
  } = useLoadSalesData(selectedMetrics);

  const {
    loadSalesTable: loadMoreSalesTable,
    abort: abortLoadMoreSalesTable,
    loading: loadingLoadMore,
  } = useLoadSalesData(selectedMetrics);
  const {
    loadSalesTable: loadTableHeadingRow,
    abort: abortTableHeadingRow,
    loading: headerLoading,
  } = useLoadSalesData(selectedMetrics);
  const [headingRow, setHeadingRow] = useState<SalesTableVisualRow>();
  const { isDefaultSelectAll } = useDefaultTableSelectionIsSelectAll();

  const prevCompareDateRange = usePrevious(compareDateRange);

  const compareDateRangeInterval = useMemo(
    () =>
      compareDateRange !== null
        ? deriveComparisonDateRange(
            dateRangeInterval,
            compareDateRange,
            user.timezone,
            timeGranularity,
          )
        : null,
    [dateRangeInterval, compareDateRange, user.timezone, timeGranularity],
  );

  const { salesTableBenchmarks, loadingSalesTableBenchmarks } =
    useSalesTableBenchmarks(attributionModel);

  const metricsById = useMemo(() => keyBy(metrics, "id"), [metrics]);

  const tableRows = useMemo(
    () =>
      loadingTable
        ? []
        : rows.map((row) =>
            row.rowType === "normal"
              ? enhanceRowWithBenchmarks(
                  row,
                  salesTableBenchmarks,
                  metricsById,
                  user.currencies.currentCurrency,
                )
              : row,
          ),
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [loadingTable, rows, salesTableBenchmarks, metricsById],
  );

  const { selectsTopRowsIfConditionsMet } = useTableRowSelection(
    rows,
    activeTab,
  );
  const headingRowVisualized = useMemo(
    () => (!headerLoading ? headingRow : defaultRow),
    [defaultRow, headerLoading, headingRow],
  );

  const loadDataHeadingRow = useCallback(async () => {
    abortTableHeadingRow();
    const queryVariables = {
      ...(activeTab !== "breakdown" && { deepestAtomicity: null }),
      ...(activeTab === "breakdown" && {
        deepestAtomicity: breakdowns.length
          ? breakdowns?.map((i) => i.key)
          : ["Platform (Northbeam)"],
      }),

      queryType: isSplitView
        ? SalesTableTypeEnum.specific
        : !isDefaultSelectAll
        ? SalesTableTypeEnum.exclusive
        : SalesTableTypeEnum.inclusive,

      atomicity: null,
      aggregateAll: true,
      sorting: null,
    };

    const data = await loadTableHeadingRow({
      ...queryVariables,
      compareDateRange: compareDateRangeInterval,
    });

    if (data.isRequestAborted) {
      return;
    }

    if (data.queryMetadata) {
      addQueryMetadata({
        context: "Sales v3",
        topic: "Table Heading Row",
        metadata: data.queryMetadata,
      });
    }

    const processedCompareData: SalesTableVisualRow[] =
      data.comparison?.rows?.map((ad, index) => {
        return {
          ...transform({
            row: processTableData(ad, nbOnlyMetrics, customMetrics),
            breakdowns,
            tab: activeTab,
            metricsById,
            currentBreakdown: null,
            code: user.currencies.currentCurrency,
          }),
          index,
        };
      }) ?? [];

    const actualRows = data?.actual?.rows ?? [];

    const processedData = actualRows.map((ad, index) => ({
      ...transform({
        row: processTableData(ad, nbOnlyMetrics, customMetrics),
        breakdowns,
        tab: activeTab,
        metricsById,
        currentBreakdown: null,
        code: user.currencies.currentCurrency,
        compareRow: processedCompareData?.[index],
      }),
      index,
    }));

    setTabState((_tabState) => ({
      ..._tabState,
      totalCount: actualRows[0]?.deepestAtomicityCounts ?? 0,
    }));

    setHeadingRow(() => processedData[0]);

    /* eslint-disable react-hooks/exhaustive-deps */
  }, [
    abortTableHeadingRow,
    activeTab,
    breakdowns,
    compareDateRange,
    compareDateRangeInterval,
    isDefaultSelectAll,
    isSplitView,
    loadTableHeadingRow,
    metrics,
    metricsById,
    prevCompareDateRange,
    setTabState,
    /* eslint-enable react-hooks/exhaustive-deps */
  ]);

  const maxIndentLevel = useMemo(
    () =>
      activeTab === "breakdown" ? breakdowns.length - 1 : breakdowns.length,
    [activeTab, breakdowns.length],
  );

  // construct sales dictionary
  const updateSalesDictionary = useCallback(
    (rows: SalesTableRow[]) => {
      const newSalesDictionary: SalesRowDictionary = {};
      constructSalesDictionary(maxIndentLevel, rows, newSalesDictionary);
      salesRowDictionary.current = newSalesDictionary;
    },
    [maxIndentLevel],
  );

  const [exportModalOpened, setExportModalOpened] = useState(false);

  const loadData = useCallback(
    async (options?: {
      offset: number;
      breakdownPath: string[];
      append: boolean;
      preload: boolean;
    }) => {
      const { offset, breakdownPath, append, preload } = options ?? {};
      const shouldPreload =
        preload && (breakdowns.length > 0 || activeTab === "breakdown");

      let atomicity: string[] | null = breakdowns.length
        ? [breakdowns[0].key]
        : null;

      if (shouldPreload) {
        atomicity = breakdowns.map((e) => e.key);
      }

      if (breakdownPath?.length) {
        if (breakdownPath.length >= breakdowns?.length) {
          atomicity = null;
        } else {
          const breakdownsSet = new Set<string>();

          breakdownPath.map((b) =>
            Object.keys(b).forEach((k) => breakdownsSet.add(k)),
          );

          atomicity = breakdowns
            .filter((b) => breakdownsSet.has(b.key))
            .map((b) => b.key);
          atomicity.push(breakdowns[atomicity.length].key);
        }
      }

      const deepestAtomicity: string[] = [];
      if (activeTab === "breakdown" && !atomicity?.length) {
        if (!atomicity) {
          atomicity = [];
        }
        atomicity.push("Platform (Northbeam)");
        deepestAtomicity.push("Platform (Northbeam)");
      }

      if (append) {
        // Don't abort since you can have multiple going simultaneously
      } else {
        abort();
      }

      let queryType: SalesTableTypeEnum;
      if (isSplitView) {
        queryType = SalesTableTypeEnum.specific;
      } else if (!isDefaultSelectAll) {
        queryType = SalesTableTypeEnum.exclusive;
      } else {
        queryType = SalesTableTypeEnum.inclusive;
      }

      const queryVariables = {
        atomicity,
        queryType,
        ...(activeTab === "breakdown" &&
          deepestAtomicity?.length && { deepestAtomicity }),
        ...(activeTab !== "breakdown" && { deepestAtomicity: null }),
        ...(offset && { offset }),
        ...(shouldPreload && { preload: true, limit: 1000000, sorting: null }),
        ...(append && {
          overridingInclusionBreakdownFilters: flatten(
            breakdownPath?.map((b) => {
              return Object.entries(b).map(([key, value]) => ({
                value: value as string,
                breakdown: key as string,
              }));
            }),
          ),
        }),
        ...(breakdownPath?.length && {
          inclusionBreakdownFilters: [
            flatten(
              breakdownPath?.map((b) => {
                return Object.entries(b).map(([key, value]) => ({
                  value: value as string,
                  breakdown: key as string,
                }));
              }),
            ),
          ],
        }),
      } as Partial<GetSalesReportV3AugmentedVariables>;

      let breakdownLevel = breakdowns.length ? breakdowns[0] : null;

      if (breakdowns.length && breakdownPath?.length) {
        breakdownLevel = breakdowns[breakdownPath.length];
      }

      const data = await (append ? loadMoreSalesTable : loadSalesTable)({
        ...queryVariables,
        compareDateRange: compareDateRangeInterval,
      });

      if (data.isRequestAborted) {
        return;
      }

      if (data.queryMetadata) {
        addQueryMetadata({
          context: "Sales v3",
          topic: "Table Row",
          metadata: data.queryMetadata,
        });
      }

      let processedCompareData: SalesTableVisualRow[][] | null = null;
      if (data.comparison) {
        const comparisonRows = data.comparison?.rows ?? [];

        if (shouldPreload) {
          const preloadedCompareData = processPreloadedData(
            breakdowns,
            comparisonRows,
          );
          processedCompareData = preloadedCompareData.map(({ rows }) => {
            return rows.map((ad, index) => {
              return {
                ...transform({
                  row: processTableData(ad, nbOnlyMetrics, customMetrics),
                  breakdowns,
                  tab: activeTab,
                  metricsById,
                  currentBreakdown: breakdownLevel,
                  code: user.currencies.currentCurrency,
                }),
                index,
              };
            });
          });
        } else {
          processedCompareData = [
            comparisonRows.map((ad, index) => ({
              ...transform({
                row: processTableData(ad, nbOnlyMetrics, customMetrics),
                breakdowns,
                tab: activeTab,
                metricsById,
                currentBreakdown: breakdownLevel,
                code: user.currencies.currentCurrency,
              }),
              index,
            })),
          ];
        }
      }

      const actualRows = data.actual?.rows ?? [];
      if (shouldPreload) {
        const preloadedData = processPreloadedData(breakdowns, actualRows);
        const processedDataByLevel: {
          breakdowns: TopLevelBreakdown[];
          rows: SalesTableVisualRow[];
        }[] = [];

        preloadedData.forEach((item, i) => {
          const preloadedCompareData = processedCompareData?.[i];
          const hasCompare = preloadedCompareData ? true : false;
          const compareByKey: Record<string, SalesTableVisualRow> = {};
          if (preloadedCompareData) {
            for (const compareRow of preloadedCompareData) {
              compareByKey[compareRow.key] = compareRow;
            }
          }

          processedDataByLevel.push({
            breakdowns: item.breakdowns,
            rows: item.rows.map((ad, index) => ({
              ...transform({
                row: processTableData(ad, nbOnlyMetrics, customMetrics),
                breakdowns,
                tab: activeTab,
                metricsById,
                currentBreakdown: item.breakdowns[item.breakdowns.length - 1],
                code: user.currencies.currentCurrency,
                compareRow: hasCompare
                  ? compareByKey[ad.key] || { metrics: {} }
                  : undefined,
              }),
              index,
            })),
          });
        });

        const sort = (rows: SalesTableVisualRow[]) =>
          sorting
            ? orderBy(
                rows,
                [
                  (e) => {
                    if (sorting.metric.id === FIXED_NAME_COLUMN_ID) {
                      return e.rowMetadata.naming.name;
                    } else {
                      const value = e.metrics[sorting.metric.id];
                      return isFinite(value) && !isNil(value) ? value : 0;
                    }
                  },
                ],
                [sortingOrder === "asc" ? "asc" : "desc"],
              ).map((e, index) => {
                e.index = index;
                return e;
              })
            : rows;

        for (let i = 1; i < processedDataByLevel.length; i++) {
          const processedDataPreviousLevel = processedDataByLevel[i - 1];
          const processedData = processedDataByLevel[i];

          const byDimension = groupBy(processedData.rows, (e) =>
            JSON.stringify(
              processedData.breakdowns.slice(0, -1).map(({ key }) => ({
                [key]: e.rowMetadata.dimensions[key],
              })),
            ),
          );

          for (const [dimensions, values] of Object.entries(byDimension)) {
            const parent = processedDataPreviousLevel.rows.find(
              (e: any) => e.key === dimensions,
            );

            if (parent) {
              parent.children = sort([...values]);
            }
          }
        }

        const newRows: SalesTableRow[] = sort([
          ...processedDataByLevel[0].rows,
        ]);

        setRows(() => {
          updateSalesDictionary(newRows);
          // For preselecting rows in split view
          if (isSplitView && !tabState.preselectionDone && offset === 0) {
            selectsTopRowsIfConditionsMet(newRows);
          }
          return newRows;
        });
      } else {
        const processedData = actualRows.map((ad, index) => ({
          ...transform({
            row: processTableData(ad, nbOnlyMetrics, customMetrics),
            breakdowns,
            tab: activeTab,
            metricsById,
            currentBreakdown: breakdownLevel,
            code: user.currencies.currentCurrency,
            compareRow: processedCompareData?.[0]?.[index],
          }),
          index,
        }));

        setRows((oldRows) => {
          const newRows: SalesTableRow[] = [];

          if (append) {
            const oldRowsWithLoadingRemoved = [...oldRows];
            newRows.push(...oldRowsWithLoadingRemoved);
          }

          updateSalesDictionary(newRows);

          if (breakdownPath?.length) {
            const pagingResult = findMetadataPaging(
              breakdownPath,
              salesRowDictionary.current,
              newRows,
            );

            if (!pagingResult) {
              return newRows;
            }
            const { rows } = pagingResult;

            if (rows.filter((r) => r.rowType === "normal").length && !offset) {
              return newRows;
            }
            rows.pop();
            rows.push(...processedData);

            if (data.actual.hasMore) {
              rows.push({
                rowType: "loadMore",
                key: `load-more-${uuidv4()}`,
                offset: offset ? SALES_TABLE_LIMIT + offset : SALES_TABLE_LIMIT,
                breakdownPath: breakdownPath?.length ? breakdownPath : [],
              });
            }
          } else {
            newRows.pop();
            newRows.push(...processedData);
            if (data.actual.hasMore) {
              newRows.push({
                rowType: "loadMore",
                key: `load-more-${uuidv4()}`,
                offset: offset ? SALES_TABLE_LIMIT + offset : SALES_TABLE_LIMIT,
                breakdownPath: breakdownPath?.length ? breakdownPath : [],
              });
            }
          }

          updateSalesDictionary(newRows);

          // For preselecting rows in split view
          if (isSplitView && !tabState.preselectionDone && offset === 0) {
            selectsTopRowsIfConditionsMet(newRows);
          }

          return newRows;
        });
      }
    },
    /* eslint-disable react-hooks/exhaustive-deps */
    [
      abort,
      activeTab,
      breakdowns,
      compareDateRange,
      compareDateRangeInterval,
      isDefaultSelectAll,
      isSplitView,
      loadMoreSalesTable,
      loadSalesTable,
      metrics,
      metricsById,
      sorting,
      sortingOrder,
      updateSalesDictionary,
    ],
    /* eslint-enable react-hooks/exhaustive-deps */
  );

  const loadMore = useCallback(
    async ({
      breakdownPath,
      offset = 0,
    }: {
      breakdownPath: string[];
      offset?: number;
    }) => {
      let drillDown = null;
      let shouldLoad = false;

      setRows((rows) => {
        // use breakdown path
        const newRows = [...rows];

        // if this is the base level
        drillDown = findMetadataPaging(
          breakdownPath ?? [],
          salesRowDictionary.current,
          newRows,
        );

        let rowsToReference = newRows;
        if (drillDown) {
          rowsToReference = drillDown.rows;
        }

        /* if at the end there is a load more row, that has been clicked, then replace it with a
           loading row */
        const potentialLoadMoreRow =
          rowsToReference[rowsToReference.length - 1];

        if (
          !potentialLoadMoreRow ||
          potentialLoadMoreRow.rowType === "loadMore"
        ) {
          shouldLoad = true;
          rowsToReference.pop();
          rowsToReference.push({
            rowType: "loading",
            key: `loading-${uuidv4()}`,
            breakdownPath,
          });
        }
        return newRows;
      });

      if (!shouldLoad) {
        return;
      }

      if (drillDown) {
        await loadData({
          offset,
          breakdownPath,
          append: true,
          preload: false,
        });
      } else {
        !isNil(offset) &&
          (await loadData({
            offset,
            breakdownPath,
            append: true,
            preload: false,
          }));
      }
    },
    [loadData],
  );

  useUpdateEffect(() => {
    (async () => {
      loadDataHeadingRow();

      if (hasFeatureFlag("salesPageV3EnableBreakdownsPreload")) {
        await loadData({
          offset: 0,
          breakdownPath: [],
          append: false,
          preload: true,
        });
      } else {
        await loadData();
      }

      for (const id of expandedRowIds) {
        const breakdownPath = JSON.parse(id as string);
        await loadMore({ breakdownPath });
      }
    })();

    return () => {
      abort();
      abortTableHeadingRow();
      abortLoadMoreSalesTable();
    };
  }, [
    abort,
    abortLoadMoreSalesTable,
    abortTableHeadingRow,
    loadData,
    loadDataHeadingRow,
    loadMore,
    hasFeatureFlag,
  ]);

  const onActivate = (tab: TabId) => {
    if (tab === "breakdown") {
      setGranularityAndActiveTab({ granularity: "campaign", activeTab: tab });
    } else if (tab === "adset") {
      setGranularityAndActiveTab({ granularity: "adset", activeTab: tab });
    } else if (tab === "ad") {
      setGranularityAndActiveTab({ granularity: "ad", activeTab: tab });
    } else {
      setGranularityAndActiveTab({ granularity: "campaign", activeTab: tab });
    }
  };

  const columnsWithBenchmarks = useMemo(() => {
    return columns.map((columnDefinition) => {
      if (columnDefinition.id.includes("benchmark")) {
        return {
          ...columnDefinition,
          loading: loadingSalesTableBenchmarks,
        };
      }
      return {
        ...columnDefinition,
        loading: headerLoading,
      };
    });
  }, [columns, loadingSalesTableBenchmarks, headerLoading]);

  const loading = useMemo(
    () => loadingTable || featureFlagDataLoading || isCustomGoalsDataLoading,
    [featureFlagDataLoading, loadingTable, isCustomGoalsDataLoading],
  );

  return (
    <div>
      <ConfigurationDetailsModal
        columnConfigs={columns}
        metrics={metrics}
        isOpen={isConfigurationDetailsModalOpen}
        onClose={onCloseConfigurationDetailsModal}
      />
      <div className="float-right flex flex-row space-x-2">
        {hasFeatureFlag("customMetricsV1") && !hideCustomMetrics && (
          <CustomMetrics />
        )}
        {!hideCustomizeTable && <CustomizeTableModal metrics={metrics} />}
        {!hideExportTable && (
          <IconButton
            className="font-semibold tracking-wide leading-5"
            variant="secondary"
            leftIcon={<i className="fa-regular fa-sharp fa-download" />}
            onClick={() => setExportModalOpened(true)}
          >
            EXPORT TABLE
          </IconButton>
        )}
        <ExportTableModal
          exportModalOpened={exportModalOpened}
          setExportModalOpened={setExportModalOpened}
        />
        {!hideExtraOptions && <TableViewSelector />}
      </div>
      {!hideSearchForm && (
        <div className="w-[420px] mt-8 mb-4">
          <SearchForm />
        </div>
      )}
      <Tabs activeTab={activeTab} onActivate={onActivate} />
      <div
        className={clsx(
          "h-[45rem] border-l-1 border-r-1 border-b-1 border-gray-0 relative",
        )}
      >
        <Table
          loadMore={loadMore}
          headingRow={headingRowVisualized}
          loading={loading}
          columns={columnsWithBenchmarks}
          rows={tableRows}
          maxIndentLevel={maxIndentLevel}
          onDimensionCellClick={(row) => {
            if (activeTab === "ad") {
              return;
            }
            let toTabId: TabId;

            const key = JSON.parse(row.key);
            const isBreakdownRow = Array.isArray(key);
            const currentDepth = breakdowns.findIndex(
              (b) => b.key === row.rowMetadata.breakdown,
            );

            const noBreakdowns = Array.isArray(key)
              ? (key as Dimensions[]).every((k) => !Object.keys(k).length) ||
                (breakdowns.length > 0 &&
                  currentDepth !==
                    Object.keys(row.rowMetadata.dimensions).length - 1)
              : false;

            if (noBreakdowns) {
              addToast("Uncategorized is not searchable", {
                appearance: "info",
              });
              return;
            }

            if (activeTab === "breakdown") {
              toTabId = "campaign";
            } else if (activeTab === "campaign") {
              toTabId = "adset";
            } else {
              toTabId = "ad";
            }

            const newProposedState = {
              inclusionAtomicities: isBreakdownRow
                ? [
                    {
                      atomicity: row.key,
                      count: row.rowMetadata.deepestAtomicityCounts,
                    },
                  ]
                : [],
              inclusionAdKeys: !isBreakdownRow
                ? [
                    {
                      adKey: row.key,
                      dimensions: row.rowMetadata.dimensions,
                    },
                  ]
                : [],
              exclusionAtomicities: [],
              exclusionAdKeys: [],
            } as FiltersState;

            tabStates[activeTab].setTabState((toTabState) => {
              return {
                ...toTabState,
                proposedFiltersState: newProposedState,
                selectAll: false,
              };
            });

            const platformDimension =
              row.rowMetadata.dimensions["Platform (Northbeam)"];
            if (platformDimension) {
              setTabTopPlatforms([platformDimension]);
            }

            tabStates[toTabId].setTabState((toTabState) => {
              const currentTabState = tabStates[activeTab].tabState;

              // reconcile the states
              const reconciledStates = reconcileAppliedAndProposedTabFilters(
                currentTabState.appliedFiltersState ?? null,
                newProposedState,
                isDefaultSelectAll,
              );

              const { toAddExclusions, appliedAdKeyFilters } =
                enrichInclusionsAndExclusions({
                  breakdowns,
                  reconciledStates,
                  currentTabState,
                });

              const newTabState = {
                ...toTabState,

                selectAll: false,
                preselectionDone: false,
                appliedFiltersState: {
                  // apply the reconciled state between the current
                  inclusionAtomicities: reconciledStates.inclusionAtoms,
                  exclusionAtomicities: [
                    ...reconciledStates.exclusionAtoms,
                    ...toAddExclusions,
                  ],
                  campaignExclusionAdKeys:
                    currentTabState?.appliedFiltersState
                      ?.campaignExclusionAdKeys,
                  campaignInclusionAdKeys:
                    appliedAdKeyFilters?.campaignInclusionFilters,
                  adsetInclusionAdKeys:
                    appliedAdKeyFilters?.adsetInclusionFilters,
                  adsetExclusionAdKeys:
                    currentTabState?.appliedFiltersState?.adsetExclusionAdKeys,
                  ...(!isBreakdownRow && {
                    [`${activeTab}InclusionAdKeys`]: [
                      {
                        adKey: row.key,
                        dimensions: row.rowMetadata.dimensions,
                      },
                    ],
                  }),
                },
              } as TabState;

              prettyLogEventPage({
                domain: "Sales Page V3",
                action: "onClick",
                additionalActionInfo: "cell-tab-state-change",
              });
              return newTabState;
            });

            onActivate(toTabId);

            if (isBreakdownRow) {
              const breakdownKeys = JSON.parse(row.key) as Dimensions[];
              if (!breakdownKeys.length) {
                return;
              }
              const expandedIds = breakdownKeys.map((_, index) => {
                return JSON.stringify(breakdownKeys.slice(0, index + 1));
              });

              setExpandedRowIds(expandedIds);
            } else {
              const dimensions = row.rowMetadata.dimensions as Dimensions;
              const breakdownPaths = breakdowns.map((_, index) =>
                JSON.stringify(
                  breakdowns.slice(0, index + 1).map((b) => {
                    return { [b.key]: dimensions[b.key] };
                  }),
                ),
              );

              setExpandedRowIds([...breakdownPaths]);
            }
          }}
        />
      </div>
    </div>
  );
}
